import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  FormHeader,
  FormInput,
  PrimarySecondaryButtonContainer,
  SubmitButton,
} from '../components/FormComponents';
import { FormErrorAlert } from '@/components/FormErrorAlert';
import { selectAdvisorAdminPortalState } from '@/model/selectors';
import {
  postAdvisorForAdminSearchRequest,
  postAdvisorFieldUpdateByAdvisorIdRequest,
} from '@/model/advisorAdminPortalModel';
import { FeatureFlagsEditor } from './FeatureFlagsEditor';
import { Card } from '@/components/ui/card';

const schema = z.object({
  advisorQuery: z.string().min(1, 'Advisor ID or Email is required'),
});

export function AdminAdvisorFeatureFlagsForm() {
  const [featureFlags, setFeatureFlags] = useState<string>('{}');
  const dispatch = useDispatch();

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<z.infer<typeof schema>>({
    resolver: zodResolver(schema),
  });

  const { status, errorMessage, selectedAdvisor, advisorFetchStatus, advisorFetchError } =
    useSelector(selectAdvisorAdminPortalState);

  // Update feature flags when advisor is loaded
  useEffect(() => {
    if (selectedAdvisor?.featureFlags) {
      setFeatureFlags(JSON.stringify(selectedAdvisor.featureFlags));
    } else if (selectedAdvisor) {
      setFeatureFlags('{}');
    }
  }, [selectedAdvisor]);

  const onSearchAdvisor = (data: z.infer<typeof schema>) => {
    const query = data.advisorQuery.trim();

    // Determine if it's an email or ID
    const isEmail = query.includes('@');

    dispatch(
      postAdvisorForAdminSearchRequest(isEmail ? { advisorEmail: query } : { advisorId: query }),
    );
  };

  const onUpdateFeatureFlags = () => {
    if (!selectedAdvisor) return;

    dispatch(
      postAdvisorFieldUpdateByAdvisorIdRequest({
        advisorId: selectedAdvisor.advisorId,
        advisorFieldKey: 'featureFlags',
        advisorFieldValue: featureFlags,
      }),
    );
  };


  return (
    <div className="space-y-6">
      <div className="mb-4 space-y-2">
        <FormHeader
          title="Manage Feature Flags"
          className="mb-0"
        />
        <p className="text-gray-600">
          Search for an advisor by ID or email, then manage their feature flags.
        </p>
      </div>

      {/* Search Form */}
      <form
        onSubmit={handleSubmit(onSearchAdvisor)}
        className="space-y-4"
      >
        <FormInput
          label="Advisor ID or Email"
          id="advisorQuery"
          type="text"
          placeholder="Enter advisor ID or email address"
          error={errors.advisorQuery}
          register={register('advisorQuery')}
          required={true}
        />

        <PrimarySecondaryButtonContainer>
          <SubmitButton
            label="Search Advisor"
            isLoading={advisorFetchStatus === 'loading'}
          />
        </PrimarySecondaryButtonContainer>

        {advisorFetchError && (
          <FormErrorAlert
            mainErrorMessage={advisorFetchError}
            className="mt-4"
          />
        )}
      </form>

      {/* Advisor Info & Feature Flags */}
      {selectedAdvisor && (
        <Card className="space-y-4 p-6">
          <div className="border-b pb-4">
            <h3 className="text-lg font-medium">Advisor Information</h3>
            <div className="mt-2 text-sm text-gray-600">
              <p>
                <strong>ID:</strong> {selectedAdvisor.advisorId}
              </p>
              <p>
                <strong>Name:</strong> {selectedAdvisor.advisorFirstName}{' '}
                {selectedAdvisor.advisorLastName}
              </p>
              <p>
                <strong>Email:</strong> {selectedAdvisor.advisorEmail}
              </p>
              <p>
                <strong>Organization:</strong>{' '}
                {selectedAdvisor.organizationDisplayName ||
                  selectedAdvisor.organizationName ||
                  'N/A'}
              </p>
            </div>
          </div>

          <div className="space-y-4">
            <h4 className="text-base font-medium">Feature Flags</h4>
            <FeatureFlagsEditor
              value={featureFlags}
              onChange={setFeatureFlags}
              error={false}
            />

            <PrimarySecondaryButtonContainer>
              <SubmitButton
                label="Update Feature Flags"
                isLoading={status === 'loading'}
                onClick={onUpdateFeatureFlags}
              />
            </PrimarySecondaryButtonContainer>

            {errorMessage && (
              <FormErrorAlert
                mainErrorMessage={errorMessage || ''}
                isWarning={true}
                className="mt-4"
              />
            )}
          </div>
        </Card>
      )}
    </div>
  );
}
